import { useWorkspaceText } from '../interface-copy';
import { useWorkspace } from '../context';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import { WorkspaceIcon } from '../icons';
import { EmptyState, ErrorState, LoadingState } from '../primitives';
import { plural, proposalAuthor, relativeAge } from './format';
import type { TreeIndex } from './tree';
import type { PageProposal } from './types';

interface Group {
  key: string;
  /** The page the suggestions show on (a new page's parent). */
  pageId: string;
  title: string;
  where: string;
  isNewPage: boolean;
  proposals: PageProposal[];
}

function groupProposals(proposals: PageProposal[], index: TreeIndex | null): Group[] {
  const rootId = index?.root.id ?? null;
  const path = (id: string | null) => {
    const page = id ? index?.byId.get(id) : index?.root;
    if (!page || !index) return '';
    return [...index.ancestors(page.id), page].map((item) => item.title).join(' / ');
  };
  const groups = new Map<string, Group>();
  for (const proposal of proposals) {
    if (proposal.kind === 'create_page') {
      const parent = proposal.parentId ?? rootId;
      if (!parent) continue;
      groups.set(proposal.id, {
        key: proposal.id,
        pageId: parent,
        title: proposal.title,
        where: `Under ${path(proposal.parentId)}`,
        isNewPage: true,
        proposals: [proposal],
      });
      continue;
    }
    if (!proposal.pageId) continue;
    const group = groups.get(proposal.pageId);
    if (group) group.proposals.push(proposal);
    else {
      const page = index?.byId.get(proposal.pageId);
      groups.set(proposal.pageId, {
        key: proposal.pageId,
        pageId: proposal.pageId,
        title: page?.title ?? proposal.pageTitle ?? proposal.title,
        where: page?.isRoot
          ? 'Root page'
          : (index
              ?.ancestors(proposal.pageId)
              .map((item) => item.title)
              .join(' / ') ?? ''),
        isNewPage: false,
        proposals: [proposal],
      });
    }
  }
  return [...groups.values()];
}

/**
 * Knowledge → For review: every open suggestion this person may see, grouped
 * by the page it changes (a suggested new page on its own row). Each opens
 * the page with its suggestions shown.
 */
export function ReviewList({
  proposals,
  error,
  onRetry,
  index,
  canPublish,
  onReview,
}: {
  proposals: PageProposal[] | null;
  error: string;
  onRetry: () => void;
  index: TreeIndex | null;
  canPublish: boolean;
  onReview: (pageId: string, suggestionId: string) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(runtimeCopy);
  const ws = useWorkspace();
  if (error && !proposals) return <ErrorState message={error} onRetry={onRetry} />;
  if (!proposals) return <LoadingState label={uiText('Loading suggestions…')} />;
  const groups = groupProposals(proposals, index);
  if (!groups.length)
    return (
      <EmptyState
        icon="check"
        title={uiText('Nothing is waiting for review.')}
        description={uiText(
          'When someone, Poneva in a chat or a connected agent suggests a change to a page, it shows here and on the page. Nothing changes until a publisher accepts it.',
        )}
      >
        <button
          type="button"
          className="ws-button"
          onClick={() =>
            ws.send(
              'Help me review our company knowledge pages and suggest what would be useful to add or correct.',
            )
          }
        >
          {uiRich('{value1} Review knowledge in chat', {
            value1: <WorkspaceIcon name="chat" size={15} />,
          })}
        </button>
      </EmptyState>
    );
  return (
    <section className="ws-wiki-review" aria-label={uiText('Suggestions for review')}>
      <p className="ws-wiki-review-intro">
        {canPublish
          ? uiText(
              'Suggested changes from chats, meetings, colleagues and connected agents. Nothing changes until a publisher accepts it on the page.',
            )
          : uiText(
              'Your suggestions wait here until a publisher decides. The pages stay as they are until then.',
            )}
      </p>
      <ul className="ws-wiki-review-list">
        {groups.map((group) => (
          <li key={group.key} className="ws-wiki-review-row">
            <div className="ws-wiki-review-page">
              <strong>
                {group.title}
                {group.isNewPage && <span className="ws-review-status">{uiText('New page')}</span>}
              </strong>
              {group.where && <small>{group.where}</small>}
              {!group.isNewPage && (
                <small>{plural(group.proposals.length, copy.suggestion, undefined, locale)}</small>
              )}
            </div>
            <ul className="ws-wiki-review-who">
              {group.proposals.map((proposal) => {
                const author = proposalAuthor(proposal, copy);
                const age = relativeAge(proposal.createdAt, locale, Date.now(), copy);
                return (
                  <li key={proposal.id}>
                    <span>
                      <strong>{author.name}</strong>
                      {[author.detail, age]
                        .filter(Boolean)
                        .map((part) => ` · ${part}`)
                        .join('')}
                    </span>
                    {proposal.reason && <small>{proposal.reason}</small>}
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="ws-button"
              onClick={() => onReview(group.pageId, group.proposals[0].id)}
            >
              {group.isNewPage ? uiText('Review page') : uiText('Review on page')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
